import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client";
import { generateDraft, getDraft } from "../api/ai";
import { useWs } from "./useWs";

export type StreamState =
  | { status: "idle" }
  | { status: "starting" }
  | { status: "streaming"; draftId: string; text: string }
  | { status: "ready"; draftId: string; text: string }
  | { status: "error"; message: string };

export function useStreamingDraft(): {
  state: StreamState;
  start: (threadId: string, messageId: string) => Promise<void>;
  reset: () => void;
} {
  const [state, setState] = useState<StreamState>({ status: "idle" });
  const draftIdRef = useRef<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const finishReady = useCallback(
    (draftId: string, text: string) => {
      stopPolling();
      setState({ status: "ready", draftId, text });
    },
    [stopPolling],
  );

  const finishError = useCallback(
    (message: string) => {
      stopPolling();
      draftIdRef.current = null;
      setState({ status: "error", message });
    },
    [stopPolling],
  );

  const schedulePoll = useCallback(
    (draftId: string, attempt = 0) => {
      pollTimerRef.current = setTimeout(async () => {
        if (draftIdRef.current !== draftId) return;

        try {
          const { draft } = await getDraft(draftId);

          if (draft.draftBody.trim()) {
            finishReady(draftId, draft.draftBody);
            return;
          }

          if (draft.status === "discarded") {
            finishError("draft_generation_failed");
            return;
          }
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            finishError("draft_not_found");
            return;
          }
          if (attempt >= 2) {
            finishError("draft_status_check_failed");
            return;
          }
        }

        if (attempt >= 40) {
          finishError("draft_generation_timeout");
          return;
        }

        schedulePoll(draftId, attempt + 1);
      }, 1500);
    },
    [finishError, finishReady],
  );

  useWs((ev) => {
    if (ev.type === "ai:draft:delta" && ev.draftId === draftIdRef.current) {
      setState((prev) => {
        if (prev.status === "starting") {
          return { status: "streaming", draftId: ev.draftId, text: ev.chunk };
        }
        if (prev.status === "streaming" && prev.draftId === ev.draftId) {
          return {
            status: "streaming",
            draftId: ev.draftId,
            text: prev.text + ev.chunk,
          };
        }
        return prev;
      });
    }
    if (ev.type === "ai:draft:done" && ev.draftId === draftIdRef.current) {
      setState((prev) => {
        if (prev.status === "streaming" && prev.draftId === ev.draftId) {
          return { status: "ready", draftId: ev.draftId, text: prev.text };
        }
        return prev;
      });
    }
  });

  const start = useCallback(
    async (threadId: string, messageId: string) => {
      stopPolling();
      setState({ status: "starting" });
      try {
        const { draftId } = await generateDraft(threadId, messageId);
        draftIdRef.current = draftId;
        schedulePoll(draftId);
      } catch (err) {
        setState({
          status: "error",
          message: err instanceof Error ? err.message : "generate_failed",
        });
      }
    },
    [schedulePoll, stopPolling],
  );

  const reset = useCallback(() => {
    stopPolling();
    draftIdRef.current = null;
    setState({ status: "idle" });
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
      draftIdRef.current = null;
    };
  }, [stopPolling]);

  return { state, start, reset };
}
